/**
 * First-touch attribution capture.
 *
 * The first time a visitor lands on the site we record the campaign parameters,
 * referrer and landing path into localStorage. Later visits never overwrite it —
 * first touch wins, so someone who arrives from an ad and converts three visits
 * later is still attributed to that ad.
 *
 * Every read and write is wrapped in try/catch: a browser blocking storage
 * (Safari private mode, strict privacy extensions) must never break a form.
 *
 * Personal data is never written to a URL or read from one.
 */

const STORAGE_KEY = "laa_attribution_v1";

const PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
] as const;

export type AttributionParamKey = (typeof PARAM_KEYS)[number];

export type StoredAttribution = Partial<Record<AttributionParamKey, string>> & {
  landing_page?: string;
  referrer?: string;
};

export type Attribution = StoredAttribution & {
  page_path?: string;
  user_agent?: string;
};

function readStored(): StoredAttribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as StoredAttribution;
    return null;
  } catch {
    return null;
  }
}

function writeStored(value: StoredAttribution): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked — attribution is best effort */
  }
}

/** Call once on first client render. No-op if attribution was already captured. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    if (readStored()) return;

    const params = new URLSearchParams(window.location.search);
    const captured: StoredAttribution = {};

    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) captured[key] = value.slice(0, 255);
    }

    captured.landing_page = window.location.pathname + window.location.search;
    if (document.referrer) captured.referrer = document.referrer.slice(0, 500);

    writeStored(captured);
  } catch {
    /* never break a page over attribution */
  }
}

/** Stored first-touch attribution merged with the current page path and UA. */
export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const stored = readStored() ?? {};
  try {
    return {
      ...stored,
      page_path: window.location.pathname,
      user_agent: window.navigator.userAgent.slice(0, 500),
    };
  } catch {
    return { ...stored };
  }
}
